import { call } from '@advanza/api'
import PopUpNotification, { PopUpNotificationFolder } from '@advanza/interfaces/PopUpNotification'
import { useCurrentCountry } from 'hooks/miscHooks'
import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'

export const FIELDS = {
    USER_TARGET: 'users_target' as const,
    SPECIFIC_TARGETS: 'specific_targets' as const,
    AVAILABILITY: 'availability' as const,
    LABEL: 'label' as const,
    HEADER_IMAGE: 'header_image' as const,
    CTA_BUTTON: {
        NAME: 'name',
        URL: 'url',
        TARGET: 'target',
    } as const,
    SERVICES_TARGET: 'services_target' as const,
    STATUS: 'status' as const,
    TITLE: 'title' as const,
    DELIVERY_TYPE: 'delivery_type' as const,
} as const

export const PUSH_FIELDS = {
    PUSH_TITLE: 'push_title' as const,
    PUSH_BODY: 'push_body' as const,
    PUSH_SEND_AT: 'push_send_at' as const,
    FOLDER_ID: 'pop_up_notification_folder_id' as const,
} as const

/**
 * The country toggle writes to redux as well as the cookie, so reading it from the store lets
 * callers re-run their fetches when it changes instead of waiting for a page reload.
 */
export function useOfficeCountry(): string {
    return useCurrentCountry() || Cookies.get('_trustoo_office_country') || 'NL'
}

export function usePopUpNotification() {
    const country = useOfficeCountry()

    const fetchPopUpNotifications = async () => {
        const queryString = new URLSearchParams({
            country_code: country,
            // Archived rows are needed client-side so the Archived filter can show them without
            // a refetch.
            archived: 'all',
        }).toString()
        const response = await call(`office/pop-up-notifications/get-list?${queryString}`)
        if (!response || response.status !== 'success') {
            console.error('Failed to fetch notifications')
        }
        return response.items
    }

    const savePopUpNotification = async (data: PopUpNotification) => {
        const id = data.pop_up_notification_id ?? ''
        const formattedData = {
            ...data,
            availability: data.availability
                ? {
                      from: data.availability.from
                          ? new Date(data.availability.from).toISOString().split('T')[0]
                          : undefined,
                      to: data.availability.to
                          ? new Date(data.availability.to).toISOString().split('T')[0]
                          : undefined,
                  }
                : undefined,
        }
        const response = await call(`office/pop-up-notifications/save/` + id, {
            json: formattedData,
        })
        if (!response) {
            console.error('Failed to save notification')
        }
        return {
            ...data,
            modified: new Date().toISOString(),
            pop_up_notification_id: response.pop_up_notification_id,
            id: response.pop_up_notification_id,
            folder_name: response.folder_name ?? null,
        }
    }

    const deletePopUpNotification = async (id: number) => {
        const response = await call(`office/pop-up-notifications/delete/${id}`, {
            json: { pop_up_notification_id: id },
            method: 'post',
        })
        if (!response) {
            console.error('Failed to delete notification')
        }
        return response
    }

    const archivePopUpNotification = async (id: number, archived: boolean) => {
        const response = await call(`office/pop-up-notifications/archive/${id}`, {
            json: { archived },
            method: 'post',
        })
        if (!response) {
            console.error('Failed to archive notification')
        }
        return response
    }

    return {
        country,
        fetchPopUpNotifications,
        savePopUpNotification,
        deletePopUpNotification,
        archivePopUpNotification,
    }
}

export function usePopUpNotificationFolders() {
    const country = useOfficeCountry()
    const [folders, setFolders] = useState<PopUpNotificationFolder[]>([])

    useEffect(() => {
        const queryString = new URLSearchParams({ country_code: country }).toString()
        call(`office/pop-up-notification-folders/get-list?${queryString}`).then(
            (response: { items: PopUpNotificationFolder[] }) => setFolders(response.items ?? []),
            () => console.error('Failed to fetch notification folders'),
        )
    }, [country])

    const createFolder = async (name: string): Promise<PopUpNotificationFolder | null> => {
        const response = await call(`office/pop-up-notification-folders/save`, {
            json: { name, country_code: country },
            method: 'post',
        })

        if (!response?.folder) {
            console.error('Failed to create notification folder')
            return null
        }

        setFolders((prev) => [...prev, response.folder])

        return response.folder
    }

    return { folders, createFolder }
}
